import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import {
  assessWallClock, clockSkewMs, createAnchorAtWallClock, formatTimecode,
  framerate, needsReanchor, reanchorAfterReboot, retimeAnchor, shiftAnchor,
  timecodeAt, timecodeSpec,
  type ClockReading, type FramerateName, type TimecodeSpec,
} from "@infoflyt/shared/timecode";
import {
  byttProduksjon, erEkteProduksjon, erFroeproduksjon, glemProduksjon, hentOekt,
  hentProduksjoner, kvitterUlest, lesMistedeProduksjoner, meldUlevert, settOekt,
  settProduksjoner,
} from "./session";
import { calibrateClock, clockQuality, readClock } from "../clock/clock";
import { lesHjerteslag, startHjerteslag } from "../clock/hjerteslag";
import type { Fremhenting } from "../clock/useClockTick";
import { FROE, PRIVAT_ID } from "../config/production";
import * as db from "./db";
import * as tidskodesynk from "./tidskodesynk";
import { slettUtkast } from "./utkast";
import { slettForSerier, slettReferansebilder } from "./dokumentlager";
import {
  DEFAULT_PAGE_ORDER, type Deltaker, type LogButton, type LogEntry, type Member,
  erDokumentsidenavn, erFelt, feltId, feltTittel,
  type PageId, type PagePreference, type Production, type Session,
  type SideId,
  type StoredAnchor, type SuSvar, type TimeEntry,
} from "./types";

export interface SessionValue {
  readonly ready: boolean;
  /** Produksjonen, medlemmet og skiftet. Se state/session.ts. */
  readonly production: Production;
  readonly member: Member;
  /**
   * Faar denne personen sette produksjonens tidskode?
   *
   * JAM HOERER TEKNIKEREN TIL. Det er hen som har referansen foran seg —
   * foto, A-foto, A-lyd, TOM, lyd, grip, shader, teknisk assistent. VJ-en
   * er sin egen tekniker, og produsenten har alt.
   *
   * REGI HAR DET IKKE, og det er en omgjoering fra 27.10.2026. Brukeren:
   * «Det er aa sette opp for at det skal gjoeres feil. Det er ytterst faa
   * regissoerer som kan slikt og vet hvordan og hvorfor vi bruker
   * tidskode.» Regi kan fortsatt logge foer tidskoden er satt — dagen gaar
   * paa telefonens egen klokke, og postene flytter seg naar teknikeren
   * jammer. Se state/tidskodesynk.ts.
   *
   * PRIVAT JAMMES FRA TELEFONEN. En privat logg hoerer ingen produksjon
   * til, og da finnes det ingen tekniker aa vente paa. Ufravikelig regel.
   *
   * Dette er FLATENS svar, ikke tilgangen. Tilgangen haandheves i basen —
   * se policyen paa `tc_anchor`. Her er den for at knappen ikke skal staa
   * der og feile.
   */
  readonly kanJamme: boolean;
  readonly shootDay: string;
  readonly unitName: string;
  /** Teamets id, eller tom. Avgjoer hvilken oekt man skriver i. */
  readonly unitId: string;
  /** Satt når appen ikke fikk åpnet den lokale loggen. Aldri en blank skjerm. */
  readonly startupError: string | null;
  /** Sant når feilen er advarsel og ikke vegg — appen kan brukes likevel. */
  readonly startupErrorBypassable: boolean;
  dismissStartupError(): void;
  readonly spec: TimecodeSpec;
  readonly anchor: StoredAnchor | null;
  /** Poster uten tombstone, sortert på veggklokke. */
  readonly entries: readonly LogEntry[];
  /**
   * Loggoekten appen skriver i naa.
   *
   * Speiler `sessionId`-ref-en, som tilstand fordi flatene trenger den ved
   * tegning: skriveindikatoren sier hvilken SPALTE hos redaktoeren prikkene
   * hoerer til, og uten denne finnes det ingen vei til det tallet.
   */
  readonly oektId: string | null;
  readonly buttons: readonly LogButton[];
  readonly pages: readonly PagePreference[];
  readonly timeEntries: readonly TimeEntry[];
  /**
   * Produksjonene som er kjent paa telefonen, med «privat» sist.
   *
   * Hver er en hel oekt: produksjonen OG medlemskapet i den. Rolle hoerer
   * til produksjonen, ikke til brukeren — se CLAUDE.md.
   */
  readonly produksjoner: readonly Session[];
  /**
   * En arbeidsdag som gaar i en annen produksjon, eller null.
   *
   * Satt betyr at `startTimer` nekter: det gaar én timer per person. Flaten
   * maa si hvor den gaar, ellers blir den umulig aa finne igjen.
   */
  readonly timerAnnetSted: TimeEntry | null;
  readonly showTc: boolean;
  readonly blackout: boolean;
  /** Satt når ankeret er regnet fram over en avlivet økt eller en omstart. */
  readonly uncertainSince: number | null;

  /** Returnerer markørens id og den tidskoden den faktisk fikk. */
  addMarker(button: LogButton): { id: string; frames: number } | null;
  /**
   * Hukes et SU-/synkspoersmaal av, settes en markoer i loggen.
   *
   * `angreSvar` sletter den igjen — samme sletting regi gjoer fra
   * Logg-skjermen, med tombstone og revisjon. Haken er ikke lagret noe sted:
   * den LESES av loggen, saa de to kan ikke komme i utakt.
   */
  svarPaa(spoersmaalId: string, deltaker: Deltaker, tekst: string): void;
  angreSvar(spoersmaalId: string, deltakerId: string, tekst: string): void;
  /**
   * Skriver ned hva de svarte.
   *
   * Kan skrives, rettes og skrives om — til forskjell fra loggteksten, som
   * er append-only. Lagt til 24.10.2026: reporteren noterer svarene paa
   * nettflata, og regi skal kunne gjoere det samme i felt.
   */
  skrivSvar(svarId: string, tekst: string, spoersmaal: string): void;
  /** Avhukede spoersmaal i denne loggoekten. */
  readonly svar: readonly SuSvar[];
  withdrawEntry(id: string): void;
  addProse(text: string, at: ClockReading): void;
  editEntryText(id: string, text: string): void;
  deleteEntry(id: string): void;
  adjustAnchorTo(targetFrames: number, at: ClockReading): void;
  setButtons(next: readonly LogButton[]): void;
  setPages(next: readonly PagePreference[]): void;
  /**
   * Melder inn feltene prodleder har laget.
   *
   * Nye felt legges BAKERST OG PAASLAATT. Det foelger av regelen i skjemaet:
   * ingen rad betyr synlig — legger prodleder til en side, kommer den opp hos
   * alle, og den som ikke trenger den skrur den av selv. Et felt som alt er
   * kjent roeres ikke, saa brukerens rekkefoelge og av-valg staar.
   */
  registrerFelter(titler: readonly string[]): void;
  setShowTc(v: boolean): void;
  setBlackout(v: boolean): void;
  /** Bytter aktiv produksjon. River ned og bygger opp loggtilstanden. */
  byttTilProduksjon(productionId: string): void;
  /** Glemmer en produksjon helt paa denne telefonen, uleverte rader med. */
  glemLokalProduksjon(productionId: string): void;
  /** Endrer framerate. Virker BARE paa «privat» — se settFramerate. */
  settFramerate(navn: FramerateName): void;
  /** Starter dagen. `startedAtUtcMs` settes når regi har valgt tid selv,
      `replaceId` når en allerede åpen dag får ny starttid. */
  startTimer(startedAtUtcMs?: number, replaceId?: string): void;
  stopTimer(atUtcMs: number, note: string): void;
  deleteTimeEntry(id: string): void;
  clearTimeEntries(): void;
  sampleClock(): void;
  handleResume(m: Fremhenting): void;
  /** Lukker loggen når rapporten er laget. Skrives til audit_log. */
  closeLog(): void;
}

const SessionContext = createContext<SessionValue | null>(null);

/** Engangsryddingen av froe-produksjonen. Se oppstartsblokken. */
const FROE_RYDDET = "infoflyt.froeryddet.v1";

const PREF_SHOW_TC = "infoflyt.visTidskode.v1";
const PREF_BLACKOUT = "infoflyt.blackout.v1";

/** Liten bryter som overlever at appen lukkes. */
function usePreference(
  key: string,
  fallback: boolean,
): [boolean, (v: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? fallback : raw === "1";
    } catch {
      return fallback;
    }
  });
  const set = useCallback((v: boolean): void => {
    setValue(v);
    try {
      localStorage.setItem(key, v ? "1" : "0");
    } catch {
      // Privat modus kan nekte lagring. Valget gjelder da bare denne økta.
    }
  }, [key]);
  return [value, set];
}

const defaultPages = (): PagePreference[] =>
  DEFAULT_PAGE_ORDER.map((page, i) => ({ page, enabled: true, sortOrder: i }));

/**
 * Sider som er kommet til siden sist, lagt bakerst.
 *
 * HER LAA EN FEIL. Fantes det et lagret sidevalg, ble det brukt som det var
 * — og en ny innebygd side naadde da aldri fram til noen som hadde roert
 * oppsettet sitt én gang. Brief og Shotlist var usynlige for alle som alt
 * hadde rader i basen, uten at noe saa galt ut noe sted.
 *
 * BAKERST OG PAASLAATT. Rekkefoelgen brukeren har satt er hens, og en ny
 * side skal ikke dytte seg inn midt i den. Paaslaatt fordi det er svaret
 * egne felt alt gir: sida finnes, og den som ikke vil ha den skrur den av.
 */
function medNyeSider(lagret: readonly PagePreference[]): PagePreference[] {
  if (lagret.length === 0) return defaultPages();
  const har = new Set<string>(lagret.map((p) => p.page));
  const mangler = DEFAULT_PAGE_ORDER.filter((p) => !har.has(p));
  if (mangler.length === 0) return [...lagret];
  let neste = Math.max(...lagret.map((p) => p.sortOrder)) + 1;
  return [
    ...lagret,
    ...mangler.map((page) => ({ page, enabled: true, sortOrder: neste++ })),
  ];
}

/**
 * Skriving til basen.
 *
 * `void` paa et loefte som avvises gir bare en ubehandlet avvisning i
 * konsollen. En post som ikke ble lagret staar da i minnet og er borte ved
 * neste oppstart — uten et eneste tegn til regi. Det er nettopp det
 * `storageIsPersistent` finnes for aa hindre ved oppstart.
 */
function skriv(p: Promise<unknown>, hva: string): void {
  void p.catch((e: unknown) => {
    // eslint-disable-next-line no-console
    console.error(`[infoflyt] lagring feilet: ${hva}`, e);
    window.dispatchEvent(new CustomEvent("infoflyt:lagringsfeil", { detail: hva }));
  });
}

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  /*
   * Oekten leses synkront ved oppstart, og ligger etterpaa i tilstand.
   *
   * Appen maa kunne starte uten nett, saa oppstarten kan ikke vente paa en
   * server. Siste kjente oekt ligger lokalt; en innlogging blir en
   * oppdatering av den. Se state/session.ts.
   *
   * TILSTAND OG IKKE `useMemo(hentOekt, [])`, fordi aktiv produksjon kan
   * byttes i menyen. Den tomme dep-lista var trygg bare saa lenge oekten
   * aldri endret seg.
   */
  const [oekt, setOekt] = useState<Session>(hentOekt);
  const produksjonId = oekt.production.id;

  /*
   * Framerate og drop-frame er PRODUKSJONSEGENSKAPER.
   *
   * Her sto en tom dep-liste. Med flere produksjoner ville et bytte til en
   * produksjon med annen framerate gitt feil tidskode — ikke bare feil
   * visning, for `spec` er det all regning gaar gjennom.
   */
  const spec = useMemo(
    () => timecodeSpec(oekt.production.framerate, oekt.production.dropFrame),
    [oekt.production.framerate, oekt.production.dropFrame],
  );

  const [ready, setReady] = useState(false);
  const [startupError, setStartupError] = useState<string | null>(null);
  const [bypassable, setBypassable] = useState(false);
  const [bypassed, setBypassed] = useState(false);
  const [anchor, setAnchor] = useState<StoredAnchor | null>(null);
  const [entries, setEntries] = useState<readonly LogEntry[]>([]);
  /*
   * TEAMETS POSTER KOM NED. Da maa skjermen vise dem.
   *
   * Hendelsen kommer fra takten i App.tsx, og bare naar noe FAKTISK ble
   * hentet — ellers ville vi lest den lokale basen paa nytt hvert fjerde
   * sekund hele dagen for ingenting.
   */
  const [teamrunde, setTeamrunde] = useState(0);
  useEffect(() => {
    const paa = (): void => setTeamrunde((n) => n + 1);
    window.addEventListener("infoflyt:teamlogg", paa);
    return () => window.removeEventListener("infoflyt:teamlogg", paa);
  }, []);

  useEffect(() => {
    if (teamrunde === 0) return;
    const sid = sessionId.current;
    if (sid === null) return;
    void (async () => setEntries(await db.entriesForSession(sid)))();
  }, [teamrunde]);
  const [buttons, setButtonState] = useState<readonly LogButton[]>([]);
  const [pages, setPageState] = useState<readonly PagePreference[]>(defaultPages);
  const [timeEntries, setTimeEntries] = useState<readonly TimeEntry[]>([]);
  /*
   * En arbeidsdag som gaar i en ANNEN produksjon.
   *
   * Man kan ikke jobbe paa to produksjoner i samme oeyeblikk, saa det gaar
   * én timer av gangen per person. Men man kan jobbe dag paa én og kveld paa
   * en annen, saa timeren foelger ikke med i byttet — den blir staaende der
   * den hoerer til, og vises her slik at den ikke blir glemt.
   */
  const [timerAnnetSted, setTimerAnnetSted] = useState<TimeEntry | null>(null);

  /*
   * Speil av tilstanden, lest utenfor oppdatererne.
   *
   * React lover ikke at en oppdaterer kjoeres én gang — StrictMode kaller den
   * to ganger, og samtidig gjengiving kan spille den om igjen. Skriving til
   * basen og rader i audit_log laa inne i dem, saa én justering kunne gi to
   * anchor_adjusted-rader. audit_log er fasit for en logg som ellers bare kan
   * legges til, og da er en hendelse som aldri skjedde en feil i dataene.
   *
   * Bivirkningene skjer naa én gang, utenfor, og oppdatereren er ren.
   */
  const entriesRef = useRef<readonly LogEntry[]>([]);
  entriesRef.current = entries;
  const timeRef = useRef<readonly TimeEntry[]>([]);
  timeRef.current = timeEntries;

  /**
   * Paletten og tidskodebryteren huskes mellom økter.
   *
   * Uten det blinker appen lyst opp hver gang den åpnes, også midt på natta
   * på et mørkt sett — og det er nøyaktig det Blackout finnes for å unngå.
   * Verdiene ligger i localStorage og ikke i loggdatabasen, slik at de
   * overlever også når lagringen ikke er tilgjengelig.
   */
  const [showTc, setShowTc] = usePreference(PREF_SHOW_TC, true);
  const [blackout, setBlackout] = usePreference(PREF_BLACKOUT, false);
  const [uncertainSince, setUncertainSince] = useState<number | null>(null);

  /**
   * Uten sikker kontekst finnes verken OPFS eller crypto.randomUUID, og
   * loggen kan ikke lagres. Sjekken står i render og ikke i en effekt, fordi
   * det som feiler feiler under første render — og da er det for sent.
   */
  const insecure = typeof window !== "undefined" && !window.isSecureContext;

  /**
   * Loggøkta hentes fra lagringen, ikke lages på nytt.
   *
   * Her lå feilen: en ny id ved hver oppstart gjorde at alle poster fra
   * forrige gang ble liggende usynlige under en gammel økt.
   */
  const sessionId = useRef<string | null>(null);
  const [oektId, setOektId] = useState<string | null>(null);

  /** Stopper hjerteslaget. Settes naar oppstarten har kalibrert klokka. */
  const stoppHjerteslag = useRef<(() => void) | null>(null);

  /**
   * Sann mens en produksjon byttes.
   *
   * SIKKERHETSKRITISK. Et bytte river ned loggtilstanden og bygger den opp
   * for den nye produksjonen, og i mellomtiden peker `sessionId` ingensteds.
   * Skrev appen en markoer da, ville den havnet i forrige produksjons
   * loggoekt — eller laget en ny oekt i den gale. Alt som skriver sjekker
   * denne, og NEKTER heller enn aa finne paa noe.
   */
  const bytter = useRef(false);

  const anchorRef = useRef<StoredAnchor | null>(null);
  anchorRef.current = anchor;

  useEffect(() => {
    let cancelled = false;

    if (insecure) return;

    void (async () => {
      try {
      await db.openDb(produksjonId);

      // Loggen må ligge på disk. Er den bare i minnet, er alt borte neste
      // gang appen åpnes — og det skal ikke oppdages ved skiftslutt.
      if (!(await db.storageIsPersistent()) && !bypassed) {
        // Lar seg forbigå. En falsk positiv skal ikke kunne låse regi ute
        // midt i en opptaksdag — da er kuren verre enn sykdommen.
        setBypassable(true);
        setStartupError(
          "Loggen kan ikke lagres på telefonen, bare i minnet — alt forsvinner "
          + "når appen lukkes. Tjeneren må sende hodene Cross-Origin-Opener-Policy: "
          + "same-origin og Cross-Origin-Embedder-Policy: require-corp, og "
          + "nettleseren må støtte Origin Private File System.",
        );
        return;
      }
      // Ankeret opprettes først når klokka er kalibrert, slik at
      // monotonicMs er sammenlignbar med senere avlesninger.
      await calibrateClock();
      if (cancelled) return;

      /*
       * Hjerteslaget leses FOER skrivingen starter.
       *
       * Det foerste slaget overskriver det forrige, og det forrige er hele
       * beviset: avlesningen fra foer iOS avlivet appen. Bytter man om paa
       * disse to linjene, maaler vi veggklokka mot seg selv og faar alltid
       * «troverdig».
       */
      const hjerteslag = lesHjerteslag();
      stoppHjerteslag.current = startHjerteslag();

      const today = new Date().toISOString().slice(0, 10);
      // Utkastet til rapporten foelger loggen: samme slags innhold, samme
      // slettefrist. Det ligger utenfor basen, saa det maa tas for seg.
      /*
       * ALLE kjente produksjoner, ikke bare den appen staar i.
       *
       * Slettingen paa `end_date` er et krav fra docs/09-juridisk.md, ikke en
       * bekvemmelighet — den lokale kopien ligger paa en privat telefon. Den
       * kan derfor ikke henge paa hvilken produksjon man tilfeldigvis aapner
       * appen i: en produksjon som gikk ut mens regi jobbet paa en annen
       * ville ligget igjen til hen tilfeldigvis byttet tilbake.
       *
       * «Privat» har `endDateIso = null` og slettes aldri. Se Production.
       */
      for (const p of hentProduksjoner()) {
        const slettede = await db.wipeIfPastEndDate(
          p.production.id, p.production.endDateIso, today);
        if (slettede !== null) {
          slettUtkast(p.production.id);
          /*
           * Filene foelger radene.
           *
           * `wipeIfPastEndDate` returnerer serie-id-ene den slettet, nettopp
           * fordi den ikke kan naa OPFS selv. Uten dette ville dokumentene
           * blitt liggende paa telefonen etter at produksjonen var over —
           * og det er det sletterutinen finnes for aa hindre. Se
           * CLAUDE.md § «Dokumenter forlater aldri appen».
           */
          await slettForSerier(slettede);
          /* Referansebildene hoerer ikke til en serie og ville ellers blitt
             liggende etter at produksjonen er over.

             ⚠ `p.production.id`, IKKE `produksjonId`. Her sto den aktive
             produksjonens id inne i en loekke over alle: gikk en annen
             produksjon ut paa dato, tok ryddingen referansebildene til
             den man faktisk sto i. */
          await slettReferansebilder(p.production.id);
        }
      }

      /*
       * ========  OG DEN MAN IKKE ER MED I LENGER  ========
       *
       * Samme sletting, annen grunn: tilgangen er tatt bort. Serveren
       * nekter fra det sekundet `active_to` er satt, men den lokale
       * kopien ville ligget igjen til sluttdatoen — paa en telefon som
       * ikke lenger har noe med produksjonen aa gjoere.
       *
       * Det som ikke er synket opp, er tapt. Det er den riktige
       * avveiningen: den som er tatt av produksjonen faar ikke synke
       * opp lenger uansett, saa alternativet er ikke «beholdt», det er
       * «liggende uten at noen kan naa det». Se db.slettProduksjonLokalt.
       */
      for (const borte of lesMistedeProduksjoner()) {
        const { serier, beholdt } = await db.slettProduksjonLokalt(
          borte.id, { beholdUleverte: true });
        slettUtkast(borte.id);
        await slettForSerier(serier);
        await slettReferansebilder(borte.id);

        if (beholdt === 0) {
          /* Ingenting aa komme tilbake til. Produksjonen forsvinner
             fra lista, og det er riktig: den er ikke din lenger. */
          glemProduksjon(borte.id);
        } else {
          /* Den blir staaende, merket «ingen tilgang», saa postene som
             aldri kom fram kan leses. Og den sier fra om seg selv —
             brukeren 01.11.2026: «si fra om dem». */
          meldUlevert(borte.id, borte.navn, beholdt);
        }
      }

      /*
       * ========  OG FROEET, SOM ALDRI FANTES PAA SERVEREN  ========
       *
       * «Øya» ble slettet fra basen 30.10.2026, og laa likevel i appen:
       * `hentOekt` falt tilbake paa froeet naar lageret var tomt, saa en
       * fersk telefon startet i en produksjon ingen er medlem av.
       * Brukeren 02.11.2026: «Naa maa vi faa slettet alt som er igjen fra
       * den test-produksjonen.»
       *
       * Vakta i `session.ts` hindrer at den kommer tilbake. Dette tar det
       * som alt ligger der: poster, oekter, ankere, dokumenter, filer.
       * INGENTING BEHOLDES, til forskjell fra en produksjon man er tatt
       * ut av — froeets poster kan ikke leveres noe sted, fordi
       * produksjonen ikke finnes.
       *
       * ÉN GANG. Flagget er der for aa slippe seks DELETE-setninger ved
       * hver oppstart i appens levetid, ikke fordi ryddingen ville gjort
       * skade to ganger.
       */
      const froeId = FROE.production.id;
      if (erFroeproduksjon(froeId) && localStorage.getItem(FROE_RYDDET) === null) {
        const { serier } = await db.slettProduksjonLokalt(froeId);
        slettUtkast(froeId);
        await slettForSerier(serier);
        await slettReferansebilder(froeId);
        glemProduksjon(froeId);
        kvitterUlest(froeId);
        try {
          localStorage.setItem(FROE_RYDDET, "1");
        } catch {
          // Privat modus. Da ryddes det ved neste oppstart ogsaa, og det
          // koster ingenting naar det alt er tomt.
        }
      }

      const reading = readClock();
      let current = await db.anchorForDay(produksjonId, oekt.shootDay);

      /*
       * PRODUKSJONENS JAM GAAR FOERST, OGSAA NAAR VI IKKE HAR NOE SELV.
       *
       * Er man paa samme produksjon, har man samme tidskode. Har noen jammet
       * — paa en annen telefon eller paa nettflata — er det DEN som gjelder,
       * og vi uttrykker den paa vaar egen klokke. Se state/tidskodesynk.ts.
       *
       * REKKEFOELGEN ER HELE POENGET. Laget vi vaart eget anker foerst, ble
       * DET den nyeste jammen i produksjonen — en telefon som bare ble
       * aapnet ville dermed overstyrt tidskoden for alle andre. Funnet i
       * proeve 25.10.2026.
       *
       * Uten nett skjer ingenting, og da staar vaart eget. Det er riktig:
       * appen skal kunne aapnes i en kjeller.
       */
      const adoptert = await tidskodesynk.hentJam(
        produksjonId, oekt.shootDay, current, reading, spec,
      );
      if (adoptert !== null) current = adoptert;

      if (current === null) {
        // Ny logg starter med tidskoden satt til nåværende klokkeslett.
        // Det finnes derfor aldri en post uten tidskode.
        current = {
          ...createAnchorAtWallClock(reading, spec, new Date()),
          id: db.newId(),
          productionId: produksjonId,
          shootDay: oekt.shootDay,
          /* KLOKKE, IKKE JAM. Ingen har jammet ennaa, saa appen gaar paa
             sin egen klokke — men den gjelder bare DENNE telefonen, og
             ingen andre adopterer den. Fram til 27.10.2026 sto den som en
             jam, og da kunne regis telefonklokke bli produksjonens
             tidskode bare ved at appen ble aapnet. */
          kind: "klokke" as const,
          derivedFrom: null,
          /* INGEN HAR JAMMET. Flata viser streker til noen gjoer det —
             dette ankeret er bare noe postene kan regnes fra. */
          jammetUtcMs: null,
        };
        await db.saveAnchor(current);
      } else if (needsReanchor(current, reading)) {
        // Tellingen har begynt på nytt. Veggklokka brukes som differanse,
        // ikke som absolutt verdi: tidskoden fortsetter der den var.
        const next: StoredAnchor = {
          ...reanchorAfterReboot(current, reading, spec),
          id: db.newId(),
          productionId: produksjonId,
          shootDay: oekt.shootDay,
          /* Samme tidskode, ny klokke. Opphavet OG slaget foelger med: en
             re-ankring er ikke en ny jam, og skal ikke bli produksjonens
             definisjon om ankeret selv var adoptert eller foreloepig. */
          kind: current.kind,
          derivedFrom: current.derivedFrom,
          /* Jammens egen tid foelger med. En omstart er ikke en ny jam,
             og alderen skal ikke nullstilles av at appen ble aapnet. */
          jammetUtcMs: current.jammetUtcMs,
        };
        await db.saveAnchor(next);
        await db.audit(produksjonId, "tc_anchor", next.id, "anchor_adjusted",
          formatTimecode(current.tcFrames, spec), formatTimecode(next.tcFrames, spec));
        /*
         * Re-ankringen skjedde — den er mekanisk riktig hver gang. Men
         * USIKKER settes bare naar veggklokka den ble regnet fram paa
         * ikke holder. Se assessWallClock.
         *
         * Vurderingen maa gjoeres mot det GAMLE ankeret, foer `current`
         * byttes ut: det nye har `reading.wallUtcMs` i seg, saa en
         * sammenligning mot det maaler veggklokka mot seg selv.
         */
        const dom = assessWallClock(current, hjerteslag, reading);
        current = next;
        if (!cancelled && dom !== "trusted") setUncertainSince(Date.now());
      }

      /* Teamets oekt naar man staar paa et team — hele teamet skriver i
         den samme, og rapporten blir teamets. Se db.sessionForDay. */
      const session = await db.sessionForDay(
        produksjonId, oekt.shootDay, oekt.unitId === "" ? null : oekt.unitId);
      sessionId.current = session.id;
      if (!cancelled) setOektId(session.id);

      const [e, v, b, p, t, aapen] = await Promise.all([
        db.entriesForSession(session.id),
        db.svarForSession(session.id),
        db.loadButtons(produksjonId),
        db.loadPagePreferences(produksjonId),
        db.loadTimeEntries(produksjonId),
        // Uten produksjonsfilter, med vilje. Se openTimerAnyProduction.
        db.openTimerAnyProduction(),
      ]);
      if (cancelled) return;

      setAnchor(current);
      setEntries(e);
      setSvar(v);
      setButtonState(b);
      setPageState(medNyeSider(p));
      setTimeEntries(t);
      setTimerAnnetSted(
        aapen !== null && aapen.productionId !== produksjonId ? aapen : null,
      );
      setReady(true);
      // Fra naa kan det skrives igjen: anker og loggoekt tilhoerer den
      // produksjonen appen staar i. Se `bytter`.
      bytter.current = false;
      } catch (err) {
        if (cancelled) return;
        setStartupError(
          "Fikk ikke åpnet den lokale loggen. "
          + (err instanceof Error ? err.message : String(err)),
        );
      }
    })();
    return () => {
      cancelled = true;
      stoppHjerteslag.current?.();
      stoppHjerteslag.current = null;
    };
    /*
     * `produksjonId` og IKKE `spec`.
     *
     * `spec` er et objekt: to produksjoner med samme framerate gir to ulike
     * objekter, saa effekten ville enten kjoert ved hver render eller ikke
     * naar den skulle. Id-en er det som faktisk avgjoer hvilken logg som
     * skal lastes.
     */
  }, [produksjonId, oekt.shootDay, spec, insecure, bypassed]);

  /*
   * Avhukede spoersmaal i denne loggoekten.
   *
   * «Stilt» leses som «svaret finnes OG posten lever». Posten slaas opp i
   * `entries`, som bare inneholder levende poster — derfor faller hele
   * synken ut av sjekklista i det SYNK-posten slettes fra Logg-skjermen,
   * uten at noe maa holdes i takt.
   */
  const [svar, setSvar] = useState<readonly SuSvar[]>([]);
  const svarRef = useRef<readonly SuSvar[]>([]);
  svarRef.current = svar;

  /*
   * Synken som er i gang.
   *
   * EN NY SYNK BEGYNNER NAAR DELTAKEREN BYTTES. Hukes tre spoersmaal av paa
   * ALI, gaar alle tre inn i samme post; velges BEN og saa ALI igjen, blir
   * det en ny post med sin egen tidskode. Det er den rimelige lesningen av
   * «én post per synk» — man sitter med én person av gangen.
   */
  const aktivSynk = useRef<{ deltakerId: string; entryId: string } | null>(null);

  const makeEntry = useCallback(
    (
      at: ClockReading,
      fields: Pick<LogEntry, "buttonId" | "label" | "color" | "text">
        & Partial<Pick<LogEntry, "participantId">>,
    ): LogEntry | null => {
      const a = anchorRef.current;
      const sid = sessionId.current;
      // NEKTER mens en produksjon byttes. En markoer skal aldri kunne havne
      // i forrige produksjons loggoekt. Se `bytter`.
      if (a === null || sid === null || bytter.current) return null;
      /*
       * OG NEKTER I EN PRODUKSJON MAN ER TATT UT AV.
       *
       * Den staar igjen i lista saa postene som aldri kom fram kan
       * leses — men lesing er alt den er. Serveren ville uansett
       * avvist raden; her slipper den aa bli skrevet i det hele tatt.
       * ÉN vakt holder for alle tre stedene som skriver: de gaar alle
       * gjennom `makeEntry` og gir opp paa null.
       */
      if (oekt.production.utenTilgang === true) return null;
      return {
        id: db.newId(),
        /* Null: dette er min egen post. Forkortelsen staar bare paa dem
           som er hentet ned fra en annen paa teamet. */
        skrevetAv: null,
        productionId: produksjonId,
        sessionId: sid,
        anchorId: a.id,
        monotonicMs: at.monotonicMs,
        bootId: at.bootId,
        wallUtcMs: at.wallUtcMs,
        tcState: timecodeAt(a, at, spec).state,
        deletedAt: null,
        // Bare en SYNK-post setter denne. Alt annet er null.
        participantId: null,
        ...fields,
      };
    },
    [spec, oekt.production.utenTilgang],
  );

  /** Markører settes alltid på nåtid, aldri på det man ser på. */
  const addMarker = useCallback((button: LogButton): { id: string; frames: number } | null => {
    const at = readClock();
    const entry = makeEntry(at, {
      buttonId: button.id, label: button.label, color: button.color, text: "",
    });
    const a = anchorRef.current;
    if (entry === null || a === null) return null;
    setEntries((prev) => [...prev, entry]);
    skriv(db.insertEntry(entry), "insertEntry");
    // Tidskoden regnes fra samme avlesning posten fikk, ikke fra den tikkede
    // visningen — ellers viser kvitteringen et annet tall enn posten.
    return { id: entry.id, frames: timecodeAt(a, at, spec).frames };
  }, [makeEntry, spec]);

  /**
   * Hukes et spoersmaal av, foeres det inn i synkens loggpost.
   *
   * ÉN POST PER SYNK, ikke én per spoersmaal. En synk er ÉN hendelse i
   * materialet — deltakeren sitter, kameraet gaar, regi gaar gjennom lista.
   * Tjue poster paa tjue sekunder ville gjort loggen uleselig og sagt
   * ingenting mer enn én post med tjue linjer. Tidskoden er synkens
   * begynnelse, og det er den man leter etter i klipp.
   *
   * Posten ser ut som en hurtigmarkoer med etiketten SYNK. Forkortelsen
   * staar paa foerste linje i teksten, for deltakere skrives som
   * forkortelser i loggen og et soek paa ALI skal treffe denne ogsaa.
   *
   * Teksten BYGGES PAA, den bygges ikke om. Skriver regi noe eget inn i
   * synkposten — «hun graat her» — skal ikke neste avhuking viske det ut.
   */
  const svarPaa = useCallback((
    spoersmaalId: string, deltaker: Deltaker, tekst: string,
  ): void => {
    const aktiv = aktivSynk.current;
    const post = aktiv !== null && aktiv.deltakerId === deltaker.id
      ? entriesRef.current.find((e) => e.id === aktiv.entryId)
      : undefined;

    let entryId: string;
    if (post === undefined) {
      const at = readClock();
      const entry = makeEntry(at, {
        buttonId: null,
        label: "SYNK",
        // Token og ikke fast verdi, saa stripa foelger paletten i blackout.
        color: "var(--tc)",
        text: `${deltaker.forkortelse}\n${tekst}`,
        participantId: deltaker.id,
      });
      if (entry === null) return;
      entryId = entry.id;
      aktivSynk.current = { deltakerId: deltaker.id, entryId };
      setEntries((prev) => [...prev, entry]);
      skriv(db.insertEntry(entry), "insertEntry");
    } else {
      entryId = post.id;
      const neste = `${post.text}\n${tekst}`;
      setEntries((prev) => prev.map((e) => (e.id === entryId ? { ...e, text: neste } : e)));
      skriv(db.updateEntryText(entryId, neste), "updateEntryText");
    }

    const v: SuSvar = {
      id: db.newId(),
      entryId,
      spoersmaalId,
      deltakerId: deltaker.id,
      atUtcMs: Date.now(),
      /* Haken settes naa, svaret skrives ofte lenge etterpaa. */
      svar: "",
      svarAtMs: null,
    };
    setSvar((prev) => [...prev, v]);
    skriv(db.lagreSvar(produksjonId, v), "lagreSvar");
  }, [makeEntry]);

  /*
   * ========  EN ANNENS POST ROERES IKKE  ========
   *
   * «En loggpost slettes bare der den ble skrevet, av den som skrev den.»
   * Teamet deler OEKT fra 29.10.2026 — de deler ikke POSTER. En post med
   * `skrevetAv` er hentet ned fra en annens telefon; originalen ligger
   * der, og en gravstein satt her ville aldri naadd den. Da viser de to
   * ulik logg uten at noe sier fra.
   *
   * Flata skjuler knappene, basen avviser raden. Dette er leddet imellom,
   * av samme grunn som jam-vakten i `adjustAnchorTo`.
   */
  const minEgen = useCallback((id: string): boolean => {
    const e = entriesRef.current.find((x) => x.id === id);
    return e !== undefined && e.skrevetAv === null;
  }, []);

  const withdrawEntry = useCallback((id: string): void => {
    if (!minEgen(id)) return;
    setEntries((prev) => prev.filter((e) => e.id !== id));
    skriv(db.tombstoneEntry(id, Date.now()), "tombstoneEntry");
  }, [minEgen]);

  /** Tidskoden fanges ved knappetrykk, ikke ved lagring. */
  const addProse = useCallback((text: string, at: ClockReading): void => {
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    const entry = makeEntry(at, { buttonId: null, label: "", color: null, text: trimmed });
    if (entry === null) return;
    setEntries((prev) => [...prev, entry]);
    skriv(db.insertEntry(entry), "insertEntry");
  }, [makeEntry]);

  const editEntryText = useCallback((id: string, text: string): void => {
    if (!minEgen(id)) return;
    const trimmed = text.trim();
    const before = entriesRef.current.find((e) => e.id === id);
    if (before !== undefined && before.text !== trimmed) {
      skriv(db.audit(produksjonId, "log_entry", id, "entry_text_edited", before.text, trimmed), "audit");
    }
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, text: trimmed } : e)));
    skriv(db.updateEntryText(id, trimmed), "updateEntryText");
  }, [minEgen]);

  /** Sletting er tombstone, aldri fysisk. */
  const deleteEntry = useCallback((id: string): void => {
    if (!minEgen(id)) return;
    const at = Date.now();
    const before = entriesRef.current.find((e) => e.id === id);
    skriv(db.audit(produksjonId, "log_entry", id, "entry_deleted",
      before === undefined ? null : JSON.stringify({ label: before.label, text: before.text }),
      null), "audit");
    setEntries((prev) => prev.filter((e) => e.id !== id));
    skriv(db.tombstoneEntry(id, at), "tombstoneEntry");
  }, [minEgen]);

  /**
   * Haken tas bort.
   *
   * Linja fjernes fra synkposten, og var den siste, slettes hele posten —
   * med tombstone og revisjon, som enhver annen sletting regi gjoer. En
   * SYNK-post uten spoersmaal er et merke som ikke lenger betyr noe.
   *
   * Teksten endres bare hvis linja staar uroert. Har regi skrevet den om,
   * faar den staa: raden forsvinner fra sjekklista, men det regi har skrevet
   * i loggen er regis.
   */
  /**
   * Skriver ned hva de svarte — OG inn i synkposten.
   *
   * ========  SVARET MAA STAA I LOGGEN  ========
   *
   * `su_answer` er et oppslag ingen leser i loggen. Det redaktoeren og
   * klipperen ser er `log_entry.text`, og der sto bare spoersmaalet: en synk
   * saa ut som en liste over hva som ble spurt om, uten ett eneste svar.
   * Rettet 25.10.2026.
   *
   * LINJA SETTES INN UNDER SITT EGET SPOERSMAAL, og resten av posten roeres
   * ikke. Teksten bygges paa her, den bygges ikke om — skriver regi noe eget
   * inn i synkposten, «hun graat her», skal ikke et svar viske det ut. Derfor
   * leter vi opp spoersmaalslinja og skriver under den, framfor aa sette
   * sammen posten paa nytt slik nettflata gjoer.
   */
  const skrivSvar = useCallback((
    svarId: string, tekst: string, spoersmaal: string,
  ): void => {
    const naa = Date.now();
    const v = svarRef.current.find((x) => x.id === svarId);
    setSvar((prev) => prev.map((x) =>
      (x.id === svarId ? { ...x, svar: tekst, svarAtMs: naa } : x)));
    skriv(db.oppdaterSvar(svarId, produksjonId, tekst, naa), "oppdaterSvar");

    if (v === undefined || spoersmaal === "") return;
    const post = entriesRef.current.find((e) => e.id === v.entryId);
    if (post === undefined) return;

    const linjer = post.text.split("\n");
    const i = linjer.lastIndexOf(spoersmaal);
    if (i === -1) return;
    const svarlinje = `– ${tekst}`;
    const neste = linjer[i + 1];
    if (neste !== undefined && neste.startsWith("– ")) {
      /* Svaret er rettet. Da byttes linja, ikke legges en til. */
      if (tekst === "") linjer.splice(i + 1, 1); else linjer[i + 1] = svarlinje;
    } else if (tekst !== "") {
      linjer.splice(i + 1, 0, svarlinje);
    }

    const ny = linjer.join("\n");
    if (ny === post.text) return;
    setEntries((prev) => prev.map((e) => (e.id === post.id ? { ...e, text: ny } : e)));
    skriv(db.updateEntryText(post.id, ny), "updateEntryText");
  }, []);

  const angreSvar = useCallback((
    spoersmaalId: string, deltakerId: string, tekst: string,
  ): void => {
    const v = svarRef.current.find(
      (x) => x.spoersmaalId === spoersmaalId && x.deltakerId === deltakerId,
    );
    if (v === undefined) return;

    setSvar((prev) => prev.filter((x) => x.id !== v.id));
    skriv(db.slettSvar(v.id), "slettSvar");

    const igjen = svarRef.current.filter(
      (x) => x.entryId === v.entryId && x.id !== v.id,
    );
    if (igjen.length === 0) {
      if (aktivSynk.current?.entryId === v.entryId) aktivSynk.current = null;
      deleteEntry(v.entryId);
      return;
    }

    const post = entriesRef.current.find((e) => e.id === v.entryId);
    if (post === undefined) return;
    const linjer = post.text.split("\n");
    const i = linjer.lastIndexOf(tekst);
    if (i < 0) return;
    linjer.splice(i, 1);
    const neste = linjer.join("\n");
    setEntries((prev) => prev.map((e) => (e.id === v.entryId ? { ...e, text: neste } : e)));
    skriv(db.updateEntryText(v.entryId, neste), "updateEntryText");
  }, [deleteEntry]);

  /**
   * Justering er en normal handling, ikke en feilretting. Den endrer ett tall
   * på ankeret og flytter alle poster.
   */
  const opptaksdag = oekt.shootDay;

  /*
   * EN JAM MIDT I SKIFTET SKAL NAA FRAM.
   *
   * Tidskoden kan justeres foer, under og etter logging — det er en normal
   * handling. Jammer regi paa én telefon, skal de andre foelge etter mens de
   * logger, ikke ved neste oppstart. Takten er den samme som utboksen
   * bruker, og vakten i `hentJam` gjoer at en runde uten ny jam koster ett
   * lite oppslag og ingen skriving.
   */
  useEffect(() => {
    if (produksjonId === PRIVAT_ID || produksjonId === "") return;
    let avbrutt = false;
    const se = (): void => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      void (async () => {
        const vaart = anchorRef.current;
        const ny = await tidskodesynk.hentJam(
          produksjonId, opptaksdag, vaart, readClock(), spec,
        ).catch(() => null);
        if (!avbrutt && ny !== null) setAnchor(ny);
      })();
    };
    const id = window.setInterval(se, 4000);
    window.addEventListener("online", se);
    return () => {
      avbrutt = true;
      window.clearInterval(id);
      window.removeEventListener("online", se);
    };
  }, [produksjonId, opptaksdag, spec]);

  const kanJammeRef = useRef(true);

  const adjustAnchorTo = useCallback((targetFrames: number, at: ClockReading): void => {
    /*
     * VAKTEN STAAR HER OGSAA, ikke bare paa knappen.
     *
     * Flata skjuler knappen, basen avviser raden. Dette er leddet imellom:
     * et kall som kommer en annen vei — en gammel fane, en tastatursnarvei,
     * en senere flate — skal ikke kunne lage en jam og faa den avvist
     * foerst naar koen proever aa sende den. Da hadde tidskoden vaert
     * endret lokalt og ikke paa serveren, og de to sagt ulike ting.
     */
    if (!kanJammeRef.current) return;
    const prev = anchorRef.current;
    if (prev !== null) {
      const current = timecodeAt(prev, at, spec).frames;
      const next: StoredAnchor = {
        ...shiftAnchor(prev, targetFrames - current, spec),
        id: prev.id,
        productionId: prev.productionId,
        shootDay: prev.shootDay,
        /* NAA er tidskoden satt. Herfra telles de 24 timene. */
        jammetUtcMs: at.wallUtcMs,
        /*
         * EN JAM SETTER TIDSKODEN FOR HELE PRODUKSJONEN.
         *
         * Er man paa samme produksjon, har man samme tidskode. Derfor
         * `jam` og `derivedFrom: null`: raden er ikke lenger avledet av
         * noe, den ER det alle andre skal adoptere. Avgjort 25.10.2026.
         *
         * SAMME `id` SOM FOER, og det er hele poenget: postene peker hit,
         * saa alt som er logget hittil i dag flytter seg med. Justering er
         * en normal handling, ikke en feilretting.
         */
        kind: "jam" as const,
        derivedFrom: null,
      };
      skriv(db.saveAnchor(next), "saveAnchor");
      skriv(db.audit(produksjonId, "tc_anchor", next.id, "anchor_adjusted",
        formatTimecode(prev.tcFrames, spec), formatTimecode(next.tcFrames, spec)), "audit");
      setAnchor(next);
    }
    // Regi har satt tidskoden på nytt. Usikkerheten er dermed besvart.
    setUncertainSince(null);
  }, [spec]);

  const setButtons = useCallback((next: readonly LogButton[]): void => {
    const normalized = next.map((b, i) => ({ ...b, sortOrder: i }));
    setButtonState(normalized);
    skriv(db.replaceButtons(produksjonId, normalized), "replaceButtons");
  }, [produksjonId]);

  const registrerFelter = useCallback((titler: readonly string[]): void => {
    setPageState((prev) => {
      /*
       * FOERST RYDDES FELT SOM HAR BLITT EN FAST SIDE.
       *
       * «Kameraplan» var et egendefinert felt foer det ble et dokumentslag.
       * Raden for feltet blir staaende i basen, og uten dette ville den vist
       * en tom side ved siden av den faste — med samme navn. Ryddingen gaar
       * foer tillegget, og den maa kjoeres ogsaa naar det ikke er noe nytt
       * aa legge til.
       */
      const uten = prev.filter(
        (p) => !erFelt(p.page) || !erDokumentsidenavn(feltTittel(p.page)));
      const kjente = new Set(uten.map((p) => p.page));
      const nye = titler
        .map(feltId)
        .filter((id) => !kjente.has(id))
        .map((id, i): PagePreference => ({
          page: id,
          enabled: true,
          sortOrder: uten.length + i,
        }));
      if (nye.length === 0 && uten.length === prev.length) return prev;
      const neste = [...uten, ...nye];
      skriv(db.replacePagePreferences(produksjonId, neste), "replacePagePreferences");
      return neste;
    });
  }, []);

  const setPages = useCallback((next: readonly PagePreference[]): void => {
    const normalized = next.map((p, i) => ({ ...p, sortOrder: i }));
    setPageState(normalized);
    skriv(db.replacePagePreferences(produksjonId, normalized), "replacePagePreferences");
  }, [produksjonId]);

  const startTimer = useCallback((startedAtUtcMs?: number, replaceId?: string): void => {
    /*
     * Én aapen arbeidstimer per PERSON, paa tvers av produksjoner.
     *
     * Serverskjemaets unike indeks kan ikke uttrykke dette alene: den gaar
     * paa (production_id, member_id), og `member_id` er selv per produksjon,
     * saa samme person har ulik id i hver. To parallelle timere slapp
     * gjennom. Se migrasjonen som legger `user_id` paa `time_entry`.
     */
    if (timerAnnetSted !== null) return;
    const startedAt = startedAtUtcMs ?? Date.now();
    if (replaceId !== undefined) {
      // Regi har rettet starttiden på en dag som allerede går.
      const found = timeRef.current.find((t) => t.id === replaceId);
      if (found !== undefined) {
        /*
         * Starttiden er satt for hånd. Da finnes det ingen måling å regne
         * fra, og den monotone avlesningen ville pekt på et annet tidspunkt
         * enn det regi oppga. Den fjernes, og varigheten faller tilbake på
         * veggklokka — som er riktig når tallet selv er oppgitt.
         */
        const moved: TimeEntry = {
          ...found, startedAtUtcMs: startedAt, monotonicMs: null, bootId: null,
        };
        skriv(db.saveTimeEntry(moved), "saveTimeEntry");
        setTimeEntries((prev) => prev.map((t) => (t.id === replaceId ? moved : t)));
      }
      return;
    }
    /*
     * Den monotone avlesningen lagres bare når dagen startes nå.
     *
     * Har regi oppgitt et klokkeslett selv, er det en verdi og ikke en
     * måling — da finnes det ingenting å telle fra.
     */
    const naa = startedAtUtcMs === undefined ? readClock() : null;
    const t: TimeEntry = {
      id: db.newId(), productionId: produksjonId,
      startedAtUtcMs: startedAt, stoppedAtUtcMs: null, note: "", source:
        startedAtUtcMs === undefined ? "app" : "manual",
      monotonicMs: naa?.monotonicMs ?? null,
      bootId: naa?.bootId ?? null,
    };
    setTimeEntries((prev) => [t, ...prev]);
    skriv(db.saveTimeEntry(t), "saveTimeEntry");
  }, [produksjonId, timerAnnetSted]);

  const stopTimer = useCallback((atUtcMs: number, note: string): void => {
    const open = timeRef.current.find((t) => t.stoppedAtUtcMs === null);
    if (open === undefined) return;
    const closed: TimeEntry = { ...open, stoppedAtUtcMs: atUtcMs, note };
    skriv(db.saveTimeEntry(closed), "saveTimeEntry");
    setTimeEntries((prev) => prev.map((t) => (t.id === closed.id ? closed : t)));
  }, []);

  const deleteTimeEntry = useCallback((id: string): void => {
    setTimeEntries((prev) => prev.filter((t) => t.id !== id));
    skriv(db.deleteTimeEntry(id), "deleteTimeEntry");
  }, []);

  const clearTimeEntries = useCallback((): void => {
    setTimeEntries((prev) => prev.filter((t) => t.stoppedAtUtcMs === null));
    skriv(db.clearTimeEntries(produksjonId), "clearTimeEntries");
  }, [produksjonId]);

  /**
   * Måler avviket mellom klokkene. Monoton klokke vinner alltid; målingen
   * sier bare hvor ofte problemet faktisk oppstår.
   */
  const sampleClock = useCallback((): void => {
    const a = anchorRef.current;
    if (a === null) return;
    const skew = clockSkewMs(a, readClock());
    if (skew !== null) skriv(db.recordClockSample(skew, clockQuality()), "recordClockSample");
  }, []);

  const handleResume = useCallback((m: Fremhenting): void => {
    const a = anchorRef.current;
    const reading = readClock();
    if (a === null) return;
    /*
     * Avviket skrives ned foer vi avgjoer noe. Dette er tallet
     * «drift maa maales, ikke antas» handler om, og en fremhenting uten
     * re-ankring er like interessant som en med.
     */
    if (m.deltaMs !== 0) {
      skriv(db.recordClockSample(m.deltaMs, clockQuality()), "recordClockSample");
    }
    if (!needsReanchor(a, reading)) return;
    const next: StoredAnchor = {
      ...reanchorAfterReboot(a, reading, spec),
      id: db.newId(),
      productionId: a.productionId,
      shootDay: a.shootDay,
      jammetUtcMs: a.jammetUtcMs,
      /* Samme tidskode, ny klokke — ikke en ny jam. */
      kind: a.kind,
      derivedFrom: a.derivedFrom,
    };
    skriv(db.saveAnchor(next), "saveAnchor");
    skriv(db.audit(produksjonId, "tc_anchor", next.id, "anchor_adjusted",
      formatTimecode(a.tcFrames, spec), formatTimecode(next.tcFrames, spec)), "audit");
    setAnchor(next);
    /*
     * Samme skille som i oppstarten: re-ankringen skjer uansett, men varselet
     * bare naar veggklokka ikke holder. En installert iOS-webapp avlives
     * flere ganger om dagen, og det er ikke i seg selv en feil.
     */
    if (assessWallClock(a, lesHjerteslag(), reading) !== "trusted") {
      setUncertainSince(Date.now());
    }
  }, [spec]);

  /** Sann naar oekten alt er lukket. Se closeLog. */
  const closed = useRef(false);

  /**
   * Bytter aktiv produksjon.
   *
   * REKKEFOELGEN ER SIKKERHETSKRITISK, og invarianten er én setning: mellom
   * foerste linje her og `setReady(true)` i oppstartseffekten skrives
   * ingenting til loggen.
   *
   * Grunnen er at `sessionId` og `anchor` hoerer til produksjonen vi forlater.
   * Ble de staaende mens den nye lastes, kunne et trykk paa en hurtigknapp
   * havnet i forrige produksjons loggoekt — og loggen kan bare legges til, saa
   * den posten ville ikke vaert mulig aa flytte etterpaa.
   */
  const byttTilProduksjon = useCallback((productionId: string): void => {
    if (productionId === produksjonId) return;
    const ny = byttProduksjon(productionId);
    // Ukjent id. Da skal ingenting skje — ikke en halv nedrigging.
    if (ny === null) return;

    /* HER kvitteres invitasjonsmerket, og ingen andre steder. Merket
       betyr «invitert, aldri vaert inne» — og det er dette trykket som
       gjoer det usant. Se state/session.ts. */
    kvitterUlest(productionId);

    // 1. Steng skrivingen.
    bytter.current = true;

    // 2. Refene peker paa den forrige produksjonen.
    sessionId.current = null;
    anchorRef.current = null;
    closed.current = false;

    // 3. Tilstanden er den forrige produksjonens logg.
    setReady(false);
    setAnchor(null);
    setEntries([]);
    setSvar([]);
    aktivSynk.current = null;
    setButtonState([]);
    setPageState(defaultPages());
    setTimeEntries([]);
    setUncertainSince(null);

    // 4. Oekten byttes. Det utloeser oppstartseffekten paa ny `produksjonId`.
    setOekt(ny);
  }, [produksjonId]);

  /**
   * Endrer framerate paa «privat».
   *
   * BARE paa privat. En produksjons framerate settes av prodleder paa
   * nettflata og er en egenskap ved produksjonen, ikke ved telefonen — to
   * regier paa samme produksjon maa regne likt. Privat har ingen prodleder,
   * saa der eier brukeren den selv.
   *
   * ANKERET REGNES OM, og det er ikke pynt. `tcFrames` er et ANTALL FRAMES,
   * og all regning gaar gjennom `spec` — ankerets egen `framerate`-kolonne
   * lagres men leses aldri. Byttet man rate uten aa regne om, ble samme tall
   * lest i en ny takt: 06:12:00:00 satt ved 25 fps ville vist 03:06:00:00 ved
   * 50 fps, og hver post i loggen flyttet seg. Derfor gjoeres tallet om via
   * millisekunder, slik at det VISTE klokkeslettet staar stille.
   *
   * Skrives til audit_log, som enhver annen endring av ankeret.
   */
  const settFramerate = useCallback((navn: FramerateName): void => {
    if (produksjonId !== PRIVAT_ID || navn === oekt.production.framerate) return;

    // Drop-frame finnes bare ved 29.97 og 59.94. Ved andre rater er den
    // meningsloes, og da skal den ikke henge igjen fra forrige valg.
    const dropFrame = framerate(navn).supportsDropFrame ? oekt.production.dropFrame : false;
    const ny = timecodeSpec(navn, dropFrame);

    const a = anchorRef.current;
    if (a !== null) {
      // Regningen ligger i packages/shared/timecode, der den skal.
      const neste: StoredAnchor = { ...a, ...retimeAnchor(a, ny) };
      skriv(db.saveAnchor(neste), "saveAnchor");
      skriv(db.audit(produksjonId, "tc_anchor", neste.id, "framerate_changed",
        `${oekt.production.framerate} fps`, `${navn} fps`), "audit");
      setAnchor(neste);
    }

    const oppdatert: Session = {
      ...oekt,
      production: { ...oekt.production, framerate: navn, dropFrame },
    };
    settOekt(oppdatert);
    // Lista maa med, ellers er raten tilbake neste gang man bytter hit.
    settProduksjoner(hentProduksjoner().map((p) =>
      (p.production.id === produksjonId ? oppdatert : p)));
    setOekt(oppdatert);
  }, [produksjonId, oekt, spec]);

  /**
   * Glem en produksjon helt paa denne telefonen.
   *
   * ========  DEN SISTE VEIEN UT  ========
   *
   * En produksjon man ikke er med i lenger blir staaende saa lenge noe
   * uleverte ligger her — det var avtalen 01.11.2026, og den staar. Men
   * da maa det finnes en vei ut, ellers ligger den til sluttdatoen
   * uansett hva brukeren mener. Brukeren 02.11.2026, om et testprosjekt
   * som aldri kommer tilbake: «Dette er et utdatert test-prosjekt som
   * skal vekk.»
   *
   * ⚠ Tar ogsaa det som ikke er levert. Det er hele poenget: her sier
   * brukeren at det ikke skal leveres.
   */
  const glemLokalProduksjon = useCallback((productionId: string): void => {
    if (!erEkteProduksjon(productionId)) return;
    void (async () => {
      /* Staar man I den, maa man ut foerst. Privat finnes alltid. */
      if (productionId === produksjonId) byttTilProduksjon(PRIVAT_ID);
      const { serier } = await db.slettProduksjonLokalt(productionId);
      slettUtkast(productionId);
      await slettForSerier(serier);
      await slettReferansebilder(productionId);
      glemProduksjon(productionId);
      kvitterUlest(productionId);
      setListeTikk((n) => n + 1);
    })();
  }, [produksjonId, byttTilProduksjon]);

  const [listeTikk, setListeTikk] = useState(0);

  /** Produksjonene som er kjent paa telefonen, med «privat» sist. */
  const produksjoner = useMemo(
    () => hentProduksjoner(),
    /* Lista leses om naar aktiv produksjon endrer seg, fordi den aktive
       alltid skal vaere med i den — og naar en produksjon er glemt, som
       ikke noedvendigvis er den man staar i. */
    [produksjonId, listeTikk],
  );

  const closeLog = useCallback((): void => {
    const sid = sessionId.current;
    if (sid === null) return;
    /*
     * Lukkes bare én gang.
     *
     * «Del rapporten på nytt» og «Endre rapporten» gaar gjennom samme
     * handling. Uten denne vakten fikk audit_log en ny open→closed-rad for
     * hver deling, og closedAtUtcMs ble overskrevet med siste tidspunkt.
     * audit_log er fasit for en logg som ellers bare kan legges til, saa en
     * overgang som aldri skjedde er en feil i dataene, ikke i visningen.
     */
    if (closed.current) return;
    closed.current = true;
    const at = Date.now();
    skriv(db.closeSession(sid, at), "closeSession");
    skriv(db.audit(produksjonId, "log_session", sid, "report_closed", "open", "closed"), "audit");
  }, []);

  const dismissStartupError = useCallback((): void => {
    setStartupError(null);
    setBypassable(false);
    setBypassed(true);
  }, []);

  const visible = useMemo(
    () => entries
      .filter((e) => e.deletedAt === null)
      .slice()
      .sort((a, b) => a.wallUtcMs - b.wallUtcMs),
    [entries],
  );

  const kanJamme = useMemo(
    () => {
      if (oekt.production.utenTilgang === true) return false;
      if (!erEkteProduksjon(oekt.production.id)) return true;
      /* Ogsaa de EKSTRA fagfunksjonene. En koordinator som i tillegg er
         A-foto jammer som A-foto — basen regner likt i tilgang.jammer,
         og en knapp som mangler her ville sendt hen til nettflata for
         noe hen har lov til. Se migrasjon 20261101110000. */
      const alle = [oekt.member.role, ...(oekt.member.extraRoles ?? [])];
      return alle.some((r) => r === "tekniker" || r === "afoto"
        || r === "vj" || r === "produsent");
    },
    [oekt.production.id, oekt.production.utenTilgang,
     oekt.member.role, oekt.member.extraRoles]);

  /* Ref og ikke verdi: `adjustAnchorTo` defineres lenger opp, og en
     avhengighet dit ville snudd rekkefoelgen. Refen leses i trykket. */
  useEffect(() => { kanJammeRef.current = kanJamme; }, [kanJamme]);

  const value = useMemo<SessionValue>(() => ({
    ready,
    startupError: insecure
      ? "Appen må åpnes over https. På en vanlig http-adresse gir ikke nettleseren "
        + "tilgang til lokal lagring, og loggen kan ikke skrives."
      : startupError,
    startupErrorBypassable: bypassable,
    dismissStartupError,
    spec, anchor, entries: visible, buttons, pages, timeEntries, oektId,
    produksjoner, timerAnnetSted,
    showTc, blackout, uncertainSince,
    production: oekt.production, member: oekt.member, kanJamme,
    shootDay: oekt.shootDay, unitName: oekt.unitName, unitId: oekt.unitId,
    addMarker, svarPaa, angreSvar, skrivSvar, svar, registrerFelter,
    withdrawEntry, addProse, editEntryText, deleteEntry, adjustAnchorTo,
    setButtons, setPages, setShowTc, setBlackout, byttTilProduksjon,
    glemLokalProduksjon, settFramerate,
    startTimer, stopTimer,
    deleteTimeEntry, clearTimeEntries, sampleClock, handleResume, closeLog,
  }), [
    oekt, kanJamme, ready, startupError, bypassable, dismissStartupError, insecure, spec, anchor, visible, buttons, pages, timeEntries,
    produksjoner, timerAnnetSted,
    showTc, blackout,
    uncertainSince, addMarker, svarPaa, angreSvar, skrivSvar, svar, registrerFelter,
    withdrawEntry, addProse, editEntryText, deleteEntry,
    oektId,
    adjustAnchorTo, setButtons, setPages, byttTilProduksjon, glemLokalProduksjon,
    settFramerate, startTimer, stopTimer, deleteTimeEntry,
    clearTimeEntries, sampleClock, handleResume, closeLog,
  ]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (v === null) throw new Error("useSession utenfor SessionProvider");
  return v;
}

export function useVisiblePages(): readonly SideId[] {
  const { pages } = useSession();
  return useMemo(() => pages.filter((p) => p.enabled).map((p) => p.page), [pages]);
}
